import { useContext, useEffect, useRef, useState } from "react";
import {
    DataGrid,
    Column,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Scrolling,
    SearchPanel,
    Paging,
    Selection,
    Toolbar,
    Item,
} from "devextreme-react/data-grid";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { useRecoilValue } from "recoil";

import { Button, FormControl, MenuItem, Pagination, Select, SelectChangeEvent } from "@mui/material";

import { createSitesClientApi, fileManagementClientApi, taxPortalClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import {
    GetAllSitesDTO,
    GetAllSitesInfoDTO,
    SelectName,
    ServiceBusEnum,
    ServiceMessage
} from "../../api-client-nswag/taxportal-client";
import {SyncTypeEnum} from "../../api-client-nswag/create-sites-client";
import { FileManagmentType } from "../../api-client-nswag/filemanagement-client";
import { updateSites } from "../UpdateScreen/UpdateSiteInfo.types";
import { dataGridConfig, defaultPageSize, refreshFlagAtom } from "../../constants";
import { calculateGridHeight, convertUTCTimeToLocal } from "../../util/Utils";
import useSnackbar from "../../hooks/useSnackbar";
import { SnackbarMessage } from "../../hooks/useSnackbar.types";
import AppNameToggle, { getAppName } from "../../cui/commonComponents/AppNameToggle";
import { IsFileManagementMicroserviceEnabledUiGet, IsFileManagementMicroserviceEnabledUiPost } from "../../envVariables";
import { isEmpty, isNull } from "lodash";

type DatagridRefType = DataGrid;

const {
    UPDATE_FILES_METADATA,
    UPDATE_FILES_LOCATION,
    DOWNLOAD_FILES,
    DOWNLOAD_ATTACHMENT_IR,
    UPLOAD_FILES,
    UPLOAD_ATTACHMENT_IR,
    UPDATE_IR_METADATA,
    SEND_FILES_TO_DUO,
    UPDATE_AND_MOVE_FILES,
} = SelectName;
const dropDownOptions = [
    UPDATE_FILES_METADATA,
    UPDATE_FILES_LOCATION,
    DOWNLOAD_FILES,
    DOWNLOAD_ATTACHMENT_IR,
    UPLOAD_FILES,
    UPLOAD_ATTACHMENT_IR,
    UPDATE_IR_METADATA,
    SEND_FILES_TO_DUO,
    UPDATE_AND_MOVE_FILES,
];
const csDropDownOptions = [UPDATE_FILES_LOCATION, SEND_FILES_TO_DUO];
const initialStateData = {
    pending: false,
    fulfilled: false,
    sites: [],
    totalCount: 0,
};

export const UpdateMetadataLocationFiles = () => {
    const { user } = useContext(OidcIdentityContext);
    const [data, setData] = useState<updateSites>(initialStateData);
    const [selectedRows, setSelectedRows] = useState<GetAllSitesDTO[]>([]);
    const [dropDown, setDropDown] = useState<string>(UPDATE_FILES_METADATA);
    const refreshFlag = useRecoilValue(refreshFlagAtom);
    const { showSnackbar } = useSnackbar();
    const dataGridRef = useRef<DatagridRefType>(null);
    const [pageNumber, setpageNumber] = useState(1);
    const [searchTerm, setSearchTerm] = useState<string | null | undefined>(null);
    const appName = useRecoilValue(getAppName);
    const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);
    const prevKeysRef = useRef<any>([]);
    const prevDataRef = useRef<any>([]);
    const REACT_APP_FILE_MANAGEMENT_MICROSERVICE_ENABLED_UI_post = IsFileManagementMicroserviceEnabledUiPost;
    const REACT_APP_FILE_MANAGEMENT_MICROSERVICE_ENABLED_UI_GET = IsFileManagementMicroserviceEnabledUiGet;
    const visibleDropDownOptions = appName?.toLowerCase() === "cs" ? csDropDownOptions : dropDownOptions;



    const fetchSites = async () => {
        setData({ pending: true, fulfilled: true, sites: [], totalCount: 0 });
        try {
            const syncType = (() => {
                if (dropDown === UPDATE_FILES_METADATA) return SyncTypeEnum.UPDATE_FILES_METADATA;
                if (dropDown === UPDATE_FILES_LOCATION) return SyncTypeEnum.UPDATE_FILES_LOCATION;
                if (dropDown === DOWNLOAD_FILES) return SyncTypeEnum.DOWNLOAD_FILES;
                if (dropDown === UPLOAD_FILES) return SyncTypeEnum.UPLOAD_FILES;
                if (dropDown === UPDATE_IR_METADATA) return SyncTypeEnum.UPDATE_IR_METADATA;
                if (dropDown === SEND_FILES_TO_DUO) return SyncTypeEnum.SEND_FILES_TO_DUO;
                if (dropDown === UPDATE_AND_MOVE_FILES) return SyncTypeEnum.UPDATE_AND_MOVE_FILES;
                if (dropDown === UPLOAD_ATTACHMENT_IR) return SyncTypeEnum.UPLOAD_ATTACHMENT_IR;
                if (dropDown === DOWNLOAD_ATTACHMENT_IR) return SyncTypeEnum.DOWNLOAD_ATTACHMENT_IR;
                return null;
            })();

            const fileManagementType = (() => {
                if (dropDown === UPDATE_FILES_METADATA) return FileManagmentType.UPDATE_FILES_METADATA;
                if (dropDown === UPDATE_FILES_LOCATION) return FileManagmentType.UPDATE_FILES_LOCATION;
                if (dropDown === DOWNLOAD_FILES) return FileManagmentType.DOWNLOAD_FILES;
                if (dropDown === UPLOAD_FILES) return FileManagmentType.UPLOAD_FILES;
                if (dropDown === UPDATE_IR_METADATA) return FileManagmentType.UPDATE_IR_METADATA;
                if (dropDown === SEND_FILES_TO_DUO) return FileManagmentType.SEND_FILES_TO_DUO;
                if (dropDown === UPDATE_AND_MOVE_FILES) return FileManagmentType.UPDATE_AND_MOVE_FILES;
                if (dropDown === UPLOAD_ATTACHMENT_IR) return FileManagmentType.UPLOAD_IR_ATTACHMENT;
                if (dropDown === DOWNLOAD_ATTACHMENT_IR) return FileManagmentType.DOWNLOAD_IR_ATTACHMENT;
                return null;
            })();

            if (syncType && fileManagementType) {
                const startIndex = pageNumber === 1 ? 0 : (pageNumber - 1) * defaultPageSize;
                const selectedAppName = appName !== null ? appName : "PERF";
                let sites: GetAllSitesInfoDTO[] | undefined;
                let totalCount: number | undefined;

                if (String(REACT_APP_FILE_MANAGEMENT_MICROSERVICE_ENABLED_UI_GET ?? "").toLowerCase() === "true") {
                    const response = await fileManagementClientApi.getFileManagementSites(
                        fileManagementType,
                        user?.profile.preferred_username,
                        selectedAppName,
                        startIndex,
                        defaultPageSize,
                        undefined
                    );
                    sites = response.data?.map((site) => ({
                        id: site.clientId,
                        hqSiteId: site.siteId,
                        mdmClientId: site.masterClientId,
                        hqClientName: site.siteName,
                        siteCreatedBy: site.siteCreatedBy,
                        siteCreated: site.siteCreatedDateTime ? new Date(site.siteCreatedDateTime) : undefined,
                        status: site.status,
                        lastModifiedBy: site.lastUpdatedBy,
                        lastModified: site.lastUpdatedDateTime ? new Date(site.lastUpdatedDateTime) : undefined,
                        message: site.message,
                    }));
                    totalCount = response.totalCount;
                } else {
                    const response = await createSitesClientApi.taxPortal_GetAllSites(
                        syncType,
                        null,
                        selectedAppName,
                        startIndex,
                        defaultPageSize
                    );
                    sites = response.data;
                    totalCount = response.totalCount;
                }

                setData({
                    ...initialStateData,
                    fulfilled: true,
                    sites,
                    totalCount,
                });
            }
        } catch (error) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
            setData(initialStateData);
        }
    };


    const onSelectionChanged = (e: any) => {
        try {
            setSelectedRows(e.selectedRowsData);

            const newPageKeys = e.selectedRowKeys;
            const newPageData = e.selectedRowsData;
            const visibleKeys = e.component?.getVisibleRows()?.map((row: any) => row.key) || [];

            const addedKeys = newPageKeys.filter((key: any) => !prevKeysRef.current.includes(key));
            const addedData = newPageData.filter((row: any) => addedKeys.includes(row.hqSiteId));
            const removedKeys = visibleKeys.filter((k: any) => prevKeysRef.current.includes(k) && !newPageKeys.includes(k));

            const updatedKeys = [
                ...prevKeysRef.current.filter((k: any) => !removedKeys.includes(k)),
                ...addedKeys,
            ];

            const updatedData = [
                ...prevDataRef.current.filter((row: any) => !removedKeys.includes(row.hqSiteId)),
                ...addedData,
            ];

            prevKeysRef.current = updatedKeys;
            prevDataRef.current = updatedData;

            setSelectedRowKeys(updatedKeys);
            setSelectedRows(updatedData);
        } catch (error) {
            clearSelection();
        }
    };

    const clearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        prevKeysRef.current = [];
        prevDataRef.current = [];
        setSelectedRows([]);
    };


    const cellRenderForDate = (e: string) => {
        if (!e) {
            return null;
        }
        return <div>{convertUTCTimeToLocal(e)}</div>;
    };
    const cellRenderForMessage = (e: any) => {
        if (e.data.message === null && e.data.status === null) {
            return null;
        }
        return <div title={e.data.message}>{e.data.status}</div>;
    };
    const handleDropDownChange = (e: SelectChangeEvent) => {
        setDropDown(e.target.value);
        clearSelection();
    };
    const sendRequest = async (eventType: ServiceBusEnum) => {
        const data = selectedRows.map(
            (e: GetAllSitesDTO): ServiceMessage => ({
                siteId: e.hqSiteId,
                userName: user?.profile.preferred_username,
                eventType,
                mdmClientId: e.mdmClientId,
                appName: appName,
            })
        );
        try {
            await taxPortalClientApi.taxPortal_InsertTopic(data);
        } catch (error) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        } finally {
            clearSelection();
        }
    };

    const runFileManagementRequest = async () => {
        const supportedActions: string[] = [
            UPDATE_FILES_METADATA,
            UPDATE_FILES_LOCATION,
            DOWNLOAD_FILES,
            DOWNLOAD_ATTACHMENT_IR,
            UPLOAD_FILES,
            UPLOAD_ATTACHMENT_IR,
            UPDATE_IR_METADATA,
            SEND_FILES_TO_DUO,
        ];
        if (!supportedActions.includes(dropDown)) {
            return false;
        }

        const userEmail = user?.profile.preferred_username;

        const requestPayload = selectedRows
            .filter(
                (row) =>
                    row.hqSiteId !== null &&
                    row.hqSiteId !== undefined &&
                    row.mdmClientId !== null &&
                    row.mdmClientId !== undefined
            )
            .map((row) => ({
                siteId: row.hqSiteId,
                clientId: row.mdmClientId,
                userEmail,
                appName: appName ?? undefined,
            }));

         if (requestPayload.length === 0) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
            clearSelection();
            return true;
        }

        if (!userEmail) {
            showSnackbar({
                message: SnackbarMessage.ERROR,
                severity: "error",
            });
            clearSelection();
            return true;
        }

        try {
            const processType = (() => {
                switch (dropDown) {
                    case UPDATE_FILES_METADATA:
                        return FileManagmentType.UPDATE_FILES_METADATA;
                    case UPDATE_FILES_LOCATION:
                        return FileManagmentType.UPDATE_FILES_LOCATION;
                    case DOWNLOAD_FILES:
                        return FileManagmentType.DOWNLOAD_FILES;
                    case UPLOAD_FILES:
                        return FileManagmentType.UPLOAD_FILES;
                    case UPDATE_IR_METADATA:
                        return FileManagmentType.UPDATE_IR_METADATA;
                    case SEND_FILES_TO_DUO:
                        return FileManagmentType.SEND_FILES_TO_DUO;
                    case UPLOAD_ATTACHMENT_IR:
                        return FileManagmentType.UPLOAD_IR_ATTACHMENT;
                    case DOWNLOAD_ATTACHMENT_IR:
                        return FileManagmentType.DOWNLOAD_IR_ATTACHMENT;
                    default:
                        return null;
                }
            })();

            if (processType) {
                await fileManagementClientApi.runProcess(processType,requestPayload);
            }
        } catch (error) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        } finally {
            clearSelection();
        }

        return true;
    };

    const handleRunClick = async () => {
        showSnackbar({ message: SnackbarMessage.UPDATE_FILE });
        if (String(REACT_APP_FILE_MANAGEMENT_MICROSERVICE_ENABLED_UI_post ?? "").toLowerCase() === "true"&& await runFileManagementRequest()) {
            return;
        }
        if (dropDown === UPDATE_FILES_METADATA) sendRequest(ServiceBusEnum.UPDATE_FILES_METADATA);
        if (dropDown === UPDATE_FILES_LOCATION) sendRequest(ServiceBusEnum.UPDATE_FILES_LOCATION);
        if (dropDown === DOWNLOAD_FILES) sendRequest(ServiceBusEnum.DOWNLOAD_FILES);
        if (dropDown === UPLOAD_FILES) sendRequest(ServiceBusEnum.UPLOAD_FILES);
        if (dropDown === UPDATE_IR_METADATA) sendRequest(ServiceBusEnum.UPDATE_IR_METADATA);
        if (dropDown === SEND_FILES_TO_DUO) sendRequest(ServiceBusEnum.SEND_FILES_TO_DUO);
        if (dropDown === UPDATE_AND_MOVE_FILES) sendRequest(ServiceBusEnum.UPDATE_AND_MOVE_FILES);
        if (dropDown === UPLOAD_ATTACHMENT_IR) sendRequest(ServiceBusEnum.UPLOAD_ATTACHMENT_IR);
        if (dropDown === DOWNLOAD_ATTACHMENT_IR) sendRequest(ServiceBusEnum.DOWNLOAD_ATTACHMENT_IR);
    };
    const refreshButtonOptions = {
        icon: "refresh",
        onClick: async () => {
            clearSelection();
            fetchSites();
        },
    };
    const handleSyncClick = async () => {
        const data = selectedRows.map((e: GetAllSitesDTO): ServiceMessage => {
            return {
                siteId: e.hqSiteId,
                userName: user?.profile.preferred_username,
                eventType: ServiceBusEnum.SYNC_CEM,
                mdmClientId: e.mdmClientId,
            };
        });
        try {
            await taxPortalClientApi.taxPortal_InsertTopic(data);
            showSnackbar({ message: SnackbarMessage.SYNC_CEM });
            clearSelection();
        } catch (error) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
    };

    useEffect(() => {
        if (searchTerm === null || searchTerm?.length === 0) {
            fetchSites();
        }
    }, [dropDown, refreshFlag, pageNumber, appName, searchTerm]);

    useEffect(() => {
        clearSelection();
    }, [appName, dropDown]);

    useEffect(() => {
        setDropDown(visibleDropDownOptions[0]);
    }, [appName]);

    const fetchSitesBySearchTerms = async () => {
        console.log("enter");
        setData({ pending: true, fulfilled: true, sites: [], totalCount: 0 });
        try {
            const syncType = (() => {
                if (dropDown === UPDATE_FILES_METADATA) return SyncTypeEnum.UPDATE_FILES_METADATA;
                if (dropDown === UPDATE_FILES_LOCATION) return SyncTypeEnum.UPDATE_FILES_LOCATION;
                if (dropDown === DOWNLOAD_FILES) return SyncTypeEnum.DOWNLOAD_FILES;
                if (dropDown === UPLOAD_FILES) return SyncTypeEnum.UPLOAD_FILES;
                if (dropDown === UPDATE_IR_METADATA) return SyncTypeEnum.UPDATE_IR_METADATA;
                if (dropDown === SEND_FILES_TO_DUO) return SyncTypeEnum.SEND_FILES_TO_DUO;
                if (dropDown === UPDATE_AND_MOVE_FILES) return SyncTypeEnum.UPDATE_AND_MOVE_FILES;
                if (dropDown === UPLOAD_ATTACHMENT_IR) return SyncTypeEnum.UPLOAD_ATTACHMENT_IR;
                if (dropDown === DOWNLOAD_ATTACHMENT_IR) return SyncTypeEnum.DOWNLOAD_ATTACHMENT_IR;
                return null;
            })();

            const fileManagementType = (() => {
                if (dropDown === UPDATE_FILES_METADATA) return FileManagmentType.UPDATE_FILES_METADATA;
                if (dropDown === UPDATE_FILES_LOCATION) return FileManagmentType.UPDATE_FILES_LOCATION;
                if (dropDown === DOWNLOAD_FILES) return FileManagmentType.DOWNLOAD_FILES;
                if (dropDown === UPLOAD_FILES) return FileManagmentType.UPLOAD_FILES;
                if (dropDown === UPDATE_IR_METADATA) return FileManagmentType.UPDATE_IR_METADATA;
                if (dropDown === SEND_FILES_TO_DUO) return FileManagmentType.SEND_FILES_TO_DUO;
                if (dropDown === UPDATE_AND_MOVE_FILES) return FileManagmentType.UPDATE_AND_MOVE_FILES;
                if (dropDown === UPLOAD_ATTACHMENT_IR) return FileManagmentType.UPLOAD_IR_ATTACHMENT;
                if (dropDown === DOWNLOAD_ATTACHMENT_IR) return FileManagmentType.DOWNLOAD_IR_ATTACHMENT;
                return null;
            })();

            if (syncType && fileManagementType) {
                const selectedAppName = appName !== null ? appName : "PERF";
                let sites: GetAllSitesInfoDTO[] | undefined;
                let totalCount: number | undefined;

                if (String(REACT_APP_FILE_MANAGEMENT_MICROSERVICE_ENABLED_UI_GET ?? "").toLowerCase() === "true") {
                    const response = await fileManagementClientApi.getFileManagementSites(
                        fileManagementType,
                        user?.profile.preferred_username,
                        selectedAppName,
                        0,
                        defaultPageSize,
                        searchTerm ?? undefined
                    );
                    sites = response.data?.map((site) => ({
                        id: site.clientId,
                        hqSiteId: site.siteId,
                        mdmClientId: site.masterClientId,
                        hqClientName: site.siteName,
                        siteCreatedBy: site.siteCreatedBy,
                        siteCreated: site.siteCreatedDateTime ? new Date(site.siteCreatedDateTime) : undefined,
                        status: site.status,
                        lastModifiedBy: site.lastUpdatedBy,
                        lastModified: site.lastUpdatedDateTime ? new Date(site.lastUpdatedDateTime) : undefined,
                        message: site.message,
                    }));
                    totalCount = response.totalCount;
                } else {
                    const response = await createSitesClientApi.taxPortal_GetAllSiteSearch(
                        syncType,
                        selectedAppName,
                        searchTerm
                    );
                    sites = response?.data;
                    totalCount = response?.totalCount;
                }

                setData({ pending: false, fulfilled: true, sites: sites ?? [], totalCount: totalCount ?? 0 });
            }
        } catch (error) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
            setData(initialStateData);
        }
    };

    useEffect(() => {
        if (!isNull(searchTerm) && !isEmpty(searchTerm)) {
            clearSelection();
            fetchSitesBySearchTerms();
        }
    }, [dropDown, refreshFlag, appName, searchTerm]);

    return (
        <div style={{ marginLeft: "20px", marginRight: "20px" }}>
            <div style={{ marginLeft: "3px", marginTop: "16px" }}>
                <div
                    style={{
                        display: "flex",
                        margin: "10px 0 10px 0",
                    }}
                >
                    <h2>Execute Files Location/Metadata Updates</h2>
                    <AppNameToggle />
                </div>
            </div>
            <DataGrid
                keyExpr="hqSiteId"
                height={calculateGridHeight(270)}
                onSelectionChanged={onSelectionChanged}
                selectedRowKeys={selectedRowKeys}
                {...dataGridConfig}
                dataSource={data.sites}
                noDataText={data.pending ? "Loading..." : "No data"}
                ref={dataGridRef}
                repaintChangesOnly={false}
            >
                <Selection mode="multiple" selectAllMode={"page"} showCheckBoxesMode={"always"} />
                <Scrolling mode="standard" />
                <LoadPanel enabled={false} />
                <SearchPanel
                    placeholder={"Global Search..."}
                    onTextChange={(e) => {
                        setSearchTerm(e);
                    }}
                    visible
                />
                <FilterRow visible={true} />
                <HeaderFilter visible={true} />
                {/* <Paging enabled={true} defaultPageSize={20} /> */}
                <Scrolling columnRenderingMode="virtual" />
                <Toolbar>
                    <Item location="start">
                        <div className="counter">
                            <h4>
                                {data.pending
                                    ? "Loading..."
                                    : `${selectedRows.length} out of ${data.totalCount} records selected`}
                            </h4>
                        </div>
                    </Item>
                    <Item location="after" visible={false}>
                        <Button onClick={handleSyncClick} disabled={!selectedRows.length} variant="outlined">
                            Sync CEM
                        </Button>
                    </Item>
                    <Item location="after">
                        <FormControl size="medium" variant="outlined" sx={{ width: 300 }}>
                            <Select
                                value={dropDown}
                                style={{ height: 36, fontSize: "14px" }}
                                defaultValue={UPDATE_FILES_METADATA}
                                onChange={handleDropDownChange}
                            >
                                {visibleDropDownOptions.map((option) => (
                                    <MenuItem key={option} value={option} style={{ fontSize: "14px" }}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Item>
                    <Item location="after">
                        <Button onClick={handleRunClick} disabled={!selectedRows.length} variant="outlined">
                            RUN
                        </Button>
                    </Item>
                    <Item name="searchPanel" location="after" />
                    <Item widget="dxButton" options={refreshButtonOptions} />
                </Toolbar>
                <Column width="70px" alignment="left" dataField="hqSiteId" caption={"SiteID"} />
                <Column width="70px" alignment="left" dataField="mdmClientId" caption={"MasterID"} />
                <Column width="70px" alignment="left" dataField="id" caption={"ClientID"} />
                <Column dataField="hqClientName" caption={"SiteName"} />
                <Column width="170px" dataField="siteCreatedBy" caption={"Site Created by"} />
                <Column
                    width="190px"
                    cellRender={(e) => cellRenderForDate(e.data.siteCreated)}
                    dataField="Site Created Date / Time"
                    caption={"Site Created Date / Time"}
                />
                <Column cellRender={cellRenderForMessage} width="70px" dataField="status" caption={"Status"} />
                <Column width="170px" dataField="lastModifiedBy" caption={"Last Updated by"} />
                <Column
                    width="190px"
                    cellRender={(e) => cellRenderForDate(e.data.lastModified)}
                    dataField="Last Update Date/Time"
                    caption={"Last Update Date/Time"}
                />
            </DataGrid>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    margin: "5px 0 0 0",
                }}
            >
                <Pagination
                    onChange={(e: any, pageNumber: any) => {
                        setpageNumber(pageNumber);
                    }}
                    count={Math.ceil((data.totalCount as number) / defaultPageSize)}
                    variant="outlined"
                    shape="rounded"
                />
            </div>
        </div>
    );
};
